export interface PlayCrop {
  /** 1 = full play visible; higher = zoomed in */
  zoom: number;
  /** Normalized focal point 0..1 */
  centerX: number;
  centerY: number;
}

export interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const DEFAULT_CROP: PlayCrop = { zoom: 1, centerX: 0.5, centerY: 0.5 };

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 4;

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

function finiteOr(defaultVal: number, raw: unknown): number {
  const n = Number(raw);
  return Number.isFinite(n) ? n : defaultVal;
}

export function normalizeCrop(crop: Partial<PlayCrop> | null | undefined): PlayCrop {
  if (!crop) return { ...DEFAULT_CROP };
  const zoom = finiteOr(MIN_ZOOM, crop.zoom);
  return {
    zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom)),
    centerX: clamp01(finiteOr(0.5, crop.centerX)),
    centerY: clamp01(finiteOr(0.5, crop.centerY)),
  };
}

export function parseCropJson(json: string | null | undefined): PlayCrop {
  if (!json) return { ...DEFAULT_CROP };
  try {
    return normalizeCrop(JSON.parse(json) as Partial<PlayCrop>);
  } catch {
    return { ...DEFAULT_CROP };
  }
}

/** Convert zoom/center into a viewBox rect in play coordinate units. */
export function cropToRect(crop: PlayCrop, playW: number, playH: number): CropRect {
  const normalized = normalizeCrop(crop);
  const w = playW / normalized.zoom;
  const h = playH / normalized.zoom;
  let x = normalized.centerX * playW - w / 2;
  let y = normalized.centerY * playH - h / 2;
  x = Math.max(0, Math.min(playW - w, x));
  y = Math.max(0, Math.min(playH - h, y));
  return { x, y, w, h };
}

/** Pan the crop center by normalized deltas (e.g. from drag in preview). */
export function panCrop(
  crop: PlayCrop,
  deltaCenterX: number,
  deltaCenterY: number,
  playW: number,
  playH: number
): PlayCrop {
  const next: PlayCrop = {
    ...normalizeCrop(crop),
    centerX: crop.centerX + deltaCenterX,
    centerY: crop.centerY + deltaCenterY,
  };
  const clamped = cropToRect(next, playW, playH);
  return {
    zoom: next.zoom,
    centerX: (clamped.x + clamped.w / 2) / playW,
    centerY: (clamped.y + clamped.h / 2) / playH,
  };
}
